// React component refactored with Bootstrap styles and all fields intact
import { useEffect, useState } from "react";
import useAuthStore from "../../store/useAuthStore";
import { useProfileUpdateStore } from "../../store/useProfileUpdateStore";
import { useUserStore } from "../../store/useUserStore";
import axios from "axios";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from 'react-select';

const mapkey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;

type MyInformationProps = {
  onSaveSuccess: () => void;              // ✅ callback to scroll
  
};

const MyInformation: React.FC<MyInformationProps> = ({ onSaveSuccess }) => {
  const { activeSection, setActiveSection, updateProfile } = useProfileUpdateStore();
  const authStore = useAuthStore.getState();
  const userId = authStore.user.id;
  const user = useUserStore((state) => state.user);
  const getUserDetails = useUserStore((state) => state.getUserDetails);

  const [name, setName] = useState(user?.first_name || "");
  const [city, setCity] = useState(user?.city || "");
  const [state, setState] = useState(user?.state || "");
  const [zipcode, setZipcode] = useState(user?.zipcode || "");
  const [distance, setDistance] = useState(user?.distance_from || 10);
  const [height, setHeight] = useState(user?.height_cm || 0);
  const [latitude, setLatitude] = useState(user?.lat || "");
  const [longitude, setLongitude] = useState(user?.lang || "");
  const [bio, setBio] = useState(user?.know_about_you || "");


  const [errors, setErrors] = useState<Errors>({});

   // State dropdown options
  const stateAbbreviations = [
    { short: "AL", full: "Alabama" },
    { short: "AK", full: "Alaska" },
    { short: "AZ", full: "Arizona" },
    { short: "AR", full: "Arkansas" },
    { short: "CA", full: "California" },
    { short: "CO", full: "Colorado" },
    { short: "CT", full: "Connecticut" },
    { short: "DE", full: "Delaware" },
    { short: "FL", full: "Florida" },
    { short: "GA", full: "Georgia" },
    { short: "HI", full: "Hawaii" },
    { short: "ID", full: "Idaho" },
    { short: "IL", full: "Illinois" },
    { short: "IN", full: "Indiana" },
    { short: "IA", full: "Iowa" },
    { short: "KS", full: "Kansas" },
    { short: "KY", full: "Kentucky" },
    { short: "LA", full: "Louisiana" },
    { short: "ME", full: "Maine" },
    { short: "MD", full: "Maryland" },
    { short: "MA", full: "Massachusetts" },
    { short: "MI", full: "Michigan" },
    { short: "MN", full: "Minnesota" },
    { short: "MS", full: "Mississippi" },
    { short: "MO", full: "Missouri" },
    { short: "MT", full: "Montana" },
    { short: "NE", full: "Nebraska" },
    { short: "NV", full: "Nevada" },
    { short: "NH", full: "New Hampshire" },
    { short: "NJ", full: "New Jersey" },
    { short: "NM", full: "New Mexico" },
    { short: "NY", full: "New York" },
    { short: "NC", full: "North Carolina" },
    { short: "ND", full: "North Dakota" },
    { short: "OH", full: "Ohio" },
    { short: "OK", full: "Oklahoma" },
    { short: "OR", full: "Oregon" },
    { short: "PA", full: "Pennsylvania" },
    { short: "RI", full: "Rhode Island" },
    { short: "SC", full: "South Carolina" },
    { short: "SD", full: "South Dakota" },
    { short: "TN", full: "Tennessee" },
    { short: "TX", full: "Texas" },
    { short: "UT", full: "Utah" },
    { short: "VT", full: "Vermont" },
    { short: "VA", full: "Virginia" },
    { short: "WA", full: "Washington" },
    { short: "WV", full: "West Virginia" },
    { short: "WI", full: "Wisconsin" },
    { short: "WY", full: "Wyoming" }
  ];

  const hitMapApiByCity = async (city: string) => {
    if (!city) return;
    try {
      // Primary: Google Maps API
      const r = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${city}&key=${mapkey}&sensor=false`);
      if (r.data.status === "OK" && r.data.results[0]) {
        const { lat, lng } = r.data.results[0].geometry.location;
        setLatitude(lat);
        setLongitude(lng);
        console.log("[Profile] Google Maps success:", lat, lng);
        
        // Attempt to fetch zip/state info if available
        const zipRes = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${mapkey}&sensor=false`
        );

        if (zipRes.status === 200 && zipRes.data.status === 'OK' && zipRes.data.results[0]) {
          const addressComponents = zipRes.data.results[0].address_components;
          const postalCode = addressComponents.find((comp: any) =>
            comp.types.includes('postal_code')
          );
          const state = addressComponents.find((comp: any) =>
            comp.types.includes('administrative_area_level_1')
          );
          
          setZipcode(postalCode?.long_name || '');
          setState(state?.short_name || '');
      }
    }
    } catch (error) {
      console.error("Error fetching zipcode:", error);
    }
  };

  const debouncedApiCall = debounce((city) => {
    if (city) hitMapApiByCity(city);
  }, 500);

  useEffect(() => {
    debouncedApiCall(city);
  }, [city]);

  useEffect(() => {
    if (!user || user.id !== userId) {
      getUserDetails(userId);
    }
  }, [userId]);

  if (activeSection !== "my_information") return null;

  type Errors = {
    name?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    latitude?: string;
    longitude?: string;
    distance?: string;
    height?: string;
    headline?: string;
    bio?: string;
  };

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name) newErrors.name = "Name is required.";
    if (!city) newErrors.city = "City is required.";
    if (!state) newErrors.state = "State is required.";
    if (!zipcode) newErrors.zipcode = "Zip code is required.";
    if (!latitude) newErrors.latitude = "Latitude is required.";
    if (!longitude) newErrors.longitude = "Longitude is required.";
    if (!distance) newErrors.distance = "Distance is required.";
    if (!height) newErrors.height = "Height is required.";
    if (!bio || bio.trim().length > 300) {
      newErrors.bio = "Bio must be 300 characters or less";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    let newheight='';
    if(height){
       // Convert cm to total inches
       const totalInches = height / 2.54;
      
       // Convert inches to feet and inches
       const feet = Math.floor(totalInches / 12);
       const inches = Math.round(totalInches % 12); 
   

      newheight= feet+'’ '+inches+'”'
    }

    const updatedData = {
      myinformation: {
        name,
        state,
        city,
        zip: zipcode,
        distance,
        height:newheight,
        height_cm:height,
        bio: bio,
        lat: Number(latitude) || 0,
        lang: Number(longitude) || 0
      },
    };
    try {
      await updateProfile(userId, updatedData);
      getUserDetails(userId);
  
      toast.success("Information updated successfully!");
      onSaveSuccess();
       setActiveSection(null); // Uncomment to close
    } catch (error) {
      console.log(error);
      
      toast.error("Failed to update Information. Please try again.");
    }
  
  };

      // Height range in inches (48 inches = 4 feet, 84 inches = 7 feet)
      const heightCm = Number(height) || 48; // assume height is in cm
     
      
      // Convert cm to total inches
      const totalInches = heightCm / 2.54;
      
      // Convert inches to feet and inches
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12); // rounding to nearest inch
      
      console.log(`Converted Height: ${feet}ft ${inches}in`);

      
   const customStyles = {
  control: (base: any) => ({
    ...base,
    backgroundColor: 'transparent', // Bootstrap gray
    borderColor: '#888888',
    borderRadius: '50px',
    minHeight: '38px',
    boxShadow: 'none',
    color:'#1a202c'
  }),
  menu: (base: any) => ({
    ...base,
   // maxHeight: '150px',
    zIndex: 9999,
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 8px',
  }),
  dropdownIndicator: (base:any ) => ({
      ...base,
      padding: '8px',
      color:'#1a202c',
       svg: {
      width: 20,
      height: 20,
      strokeWidth: 0.1, // thinner icon
    },
  })
};

const stateOptions = stateAbbreviations.map((state) => ({
  value: state.short,
  label: state.full,
}));
  return (
    <div className="card border mb-3 filter-container">
      <div className="border-bottom pb-4 mb-4 d-flex w-100 align-items-center gap-3">
        <h4 className="mb-0">My Information</h4>
        <button onClick={() => setActiveSection(null)} className="btn_close_cross_btn ms-auto">
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="accordion" id="accordionPanelsStayOpenExample">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#namePanel">
              Personal Details
            </button>
          </h2>
          <div id="namePanel" className="accordion-collapse collapse show">
            <div className="accordion-body border-bottom pb-3 mb-4">
              <label className="mb-2">Name</label>
              <input className="form-control" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter Name" />
              {errors.name && <small className="text-danger">{errors.name}</small>}
             
            </div>
          </div>
        </div>
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#locationPanel">
              Location
            </button>
          </h2>
          <div id="locationPanel" className="accordion-collapse collapse show">
            <div className="accordion-body border-bottom pb-3 mb-4">
              <p>You will receive personalized top picks based on the following details.</p>
              <label className="mb-2">City</label>
              <input className="form-control" type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Enter City" />
              {errors.city && <small className="text-danger">{errors.city}</small>}
              <label className="mb-2 mt-3">State</label>
              <Select
                components={{ IndicatorSeparator: () => null }} // This removes the |
                 classNamePrefix="react-select"
                className="form-select-container"
                options={stateOptions}
                 value={stateOptions.find(option => option.value === state) || null}
                 onChange={(selectedOption) => {
                 setState(selectedOption?.value || '');
                
                 }}
                 placeholder="State"
                 styles={customStyles}
                 />
              
              {errors.state && <small className="text-danger">{errors.state}</small>}
              <label className="mb-2 mt-3">Zip Code</label>
              <input className="form-control" type="text" value={zipcode} onChange={(e) => setZipcode(e.target.value)} />
              {errors.zipcode && <small className="text-danger">{errors.zipcode}</small>}
              <label className="mb-2 mt-3">Distance from my location</label>
              <div className="d-flex align-items-center mb-2">
                <span className="ms-auto font12">{distance} miles</span>
              </div>
              <input type="range" className="form-range" min="1" max="500" value={distance} onChange={(e) => setDistance(Number(e.target.value))} />
            </div>
          </div>
        </div>

        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed border-0 mb-0" type="button" data-bs-toggle="collapse" data-bs-target="#heightPanel">
              Height
            </button>
          </h2>
          <div id="heightPanel" className="accordion-collapse collapse show">
            <div className="accordion-body border-bottom pb-3 mb-4">
              <div className="d-flex align-items-center mb-2">
                <span className="ms-auto font12">{feet}' {inches}" ({height} cm)</span>
              </div>
              <input type="range" className="form-range" min="125" max="250" value={height} onChange={(e) => setHeight(Number(e.target.value))} />
            </div>
          </div>
        </div>

        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed border-0 mb-0" type="button" data-bs-toggle="collapse" data-bs-target="#headlinePanel">
              Bio
            </button>
          </h2>
          <div id="headlinePanel" className="accordion-collapse collapse show">
            <div className="accordion-body border-bottom pb-4 mb-4">
                <p className="mb-3">
                        Writing about yourself can be challenging, so here are some ideas to spark inspiration.
                      </p>

                      <textarea
                        className="form-control rounded-3 text-start font16 mb-3"
                        rows={12}
                        value={bio}
                        onChange={(e) => {
                          if (e.target.value.length <= 300) {
                            setBio(e.target.value);
                          }
                        }}
                        placeholder="Write something about yourself..."
                      />

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small
                          className={`font-weight-bold ${
                            bio.trim().length < 300 ? 'text-muted' : 'text-success'
                          }`}
                        >
                          {`${bio.trim().length}/300 characters`}
                        </small>
                      </div>

                    {errors.bio && <small className="text-danger">{errors.bio}</small>}

                    <p className="mb-4 pb-3">Maximum 300 characters</p>

            </div>
          </div>
        </div>
      </div>

      <button onClick={handleSave} className="btn btn-outline-primary w-100 mt-3">
        Save Changes
      </button>
    </div>
  );
};

export default MyInformation;